<div class="workspace-page br-compact-page" [class.workspace-page--full]="boardOpen"
     [style.--territory-lead-color]="mapColors.leads"
     [style.--territory-customer-color]="mapColors.customers">
  <div class="workspace-page__panel workspace-page__panel--card">
    <!-- ONE ROW, not three. The title sat alone above a toolbar, and the
         roster's own head under that carried the count and the view buttons -
         three bands of chrome over a board that wants the height. They are
         one band now: what this page is, what you can do to it, and which of
         the two ways you are reading it. -->
    <div class="workspace-page__toolbar tr-headbar">
      <h1 class="workspace-page__title tr-headbar-title">Territories</h1>

      <ng-container *ngIf="!drawing">
        <button type="button" class="br-btn br-btn--outline-primary" (click)="startFreehand()"
                matTooltip="Press and drag to draw a territory. To reshape an existing territory, select its row and click Edit boundary.">
          <br-icon>gesture</br-icon> Draw Territory
        </button>

        <!-- Same treatment as Draw Territory beside it: these are two ways to
             do one thing - add a territory, by hand or by agent - and neither
             is the senior one. Filled primary stays with the actions that
             COMMIT (Apply shape, Save the map), which can be on screen at the
             same time as these two. -->
        <button type="button" class="br-btn br-btn--outline-primary"
                [disabled]="planning" (click)="planWithAgent()"
                matTooltip="The agent clusters your located leads into balanced patches and gives each one to a rep. Territories you have edited are planned around, not replaced.">
          <br-icon>auto_awesome</br-icon>
          {{ planning ? 'Planning…' : 'Planning Territory' }}
        </button>

        <span class="tr-planning" *ngIf="planNote">
          <br-icon *ngIf="planning">auto_awesome</br-icon>{{ planNote }}
        </span>
      </ng-container>

      <ng-container *ngIf="drawing">
        <span class="tr-drawhint">
          <b class="tr-hint-go" *ngIf="sketching">Tracing — let go to close it</b>
          <ng-container *ngIf="!sketching && !draftPoints.length">
            Press and hold, then drag to trace an outline
          </ng-container>
          <ng-container *ngIf="!sketching && draftPoints.length">
            {{ draftPoints.length }} corner{{ draftPoints.length === 1 ? '' : 's' }} after tidying
          </ng-container>
          <span *ngIf="snapping" class="tr-hint-snap">· following a neighbour</span>
          <span *ngIf="snapSuspended" class="tr-hint-snap tr-hint-off">· snapping held off</span>
          <span *ngIf="workingAreaSqKm > 0"> · {{ workingAreaSqKm | number:'1.0-1' }} km²</span>
        </span>

        <button type="button" class="br-btn br-btn--ghost br-btn--sm" (click)="cancelDraw()">
          Cancel <span class="tr-kbd">Esc</span>
        </button>
      </ng-container>

      <button type="button" class="br-btn br-btn--outline-primary"
              *ngIf="selectedId != null && !drawing && !editing"
              [disabled]="!canEditBoundary" (click)="startEditing()">
        <br-icon>edit</br-icon> Edit boundary
      </button>
      <ng-container *ngIf="!drawing && editing">
        <span class="tr-drawhint">
          {{ editVertexCount }} nodes
          <b class="tr-hint-go" *ngIf="shapeChanged">
            · {{ workingAreaSqKm | number:'1.0-1' }} km² — not saved
          </b>
        </span>
        <button type="button" class="br-btn br-btn--primary br-btn--sm"
                *ngIf="shapeChanged" (click)="applyReshape()"
                matTooltip="Stage this shape. Nothing is written until you press Save the map.">
          Apply shape
        </button>
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                *ngIf="shapeChanged" [disabled]="!canUndoEdit"
                (click)="undoEdit()">Undo</button>
        <!-- Exit does not save; Apply shape stages changes in the page session. -->
        <button type="button" class="br-btn br-btn--ghost br-btn--sm" (click)="doneEditing()">
          Exit editing <span class="tr-kbd">Esc</span>
        </button>
      </ng-container>

      <!-- WHICH OF THE TWO WAYS, and how many there are. It was a Board
           button on the roster's head and a "Back to the list" on the
           board's; one pair says the same thing in one place, and says which
           you are in without either section having to carry a head of its
           own. -->
      <span class="br-view-switch" role="group" aria-label="How to show these territories">
        <button type="button" class="br-view" [class.br-view--on]="!boardOpen"
                [attr.aria-pressed]="!boardOpen"
                (click)="showBoard(false)" data-test="close-board">
          <br-icon>view_list</br-icon> List
        </button>
        <button type="button" class="br-view" [class.br-view--on]="boardOpen"
                [attr.aria-pressed]="boardOpen"
                (click)="showBoard(true)" data-test="open-board">
          <br-icon>view_kanban</br-icon> Board
        </button>
      </span>

      <mat-slide-toggle *ngIf="canFilterMine" [(ngModel)]="mineOnly">Mine</mat-slide-toggle>

      <!-- The same bar the Leads list carries, answered in the browser: this
           roster is the company's whole list of territories, fetched once, so
           there is nothing to ask a server for. See matchesFilters, which is
           written to answer the way the SQL side does.

           Only on the List view. The Board already groups by the one thing
           this bar would narrow it by, and a rule that emptied a column
           without emptying its heading would read as a broken board. -->
      <app-data-filter contextKey="territory-roster" *ngIf="!boardOpen"
                       class="tr-filters"
                       [fields]="filterFields"
                       [values]="filterValues"
                       (change)="applyFilters($event)"></app-data-filter>

      <!-- OUT OF THE PAGE. The territories chosen and every lead in each: a
           file with a row per lead beside its territory, or a sheet per
           territory to hand to the rep who works it. Which territories is
           asked in the menu, where the counts can be read before anything is
           fetched. The map is not printed.

           Words without icons, and the same words while reading. This band is
           one line at 1030px while nothing is staged - the render spec
           measures it - and an icon on each put the pair 13px over. A label
           that turned into "Reading…" would reflow the band for as long as
           the read lasts, so the progress bar under the band says it instead. -->
      <span class="tr-sheet-actions" *ngIf="!drawing && !editing">
        <button type="button" class="br-btn br-btn--outline-secondary"
                [disabled]="!rows.length || sheetBusy !== null"
                [attr.aria-busy]="sheetBusy === 'export'"
                [brMenuTriggerFor]="sheetMenu"
                [brMenuTriggerData]="{ action: 'export' }"
                data-test="export-territories">Export</button>
        <button type="button" class="br-btn br-btn--outline-secondary"
                [disabled]="!rows.length || sheetBusy !== null"
                [attr.aria-busy]="sheetBusy === 'print'"
                [brMenuTriggerFor]="sheetMenu"
                [brMenuTriggerData]="{ action: 'print' }"
                data-test="print-territories">Print</button>
      </span>

      <app-edit-session-bar *ngIf="session" [session]="session"
                            class="tr-session-bar"
                            saveLabel="Save the map"
                            [showChanges]="false"
                            (saved)="onDraftSaved()">
      </app-edit-session-bar>
    </div>

    <!-- The menu the band's Export and Print both open. Declared outside the
         band: it draws nothing where it is declared, and an item of no size
         still takes a gap in a band that is measured to the pixel. -->
    <ng-template #sheetMenu let-action="action">
      <br-menu aria-label="Lead sheets">
        <button brMenuItem *ngIf="selected as one"
                (triggered)="runSheets(action, 'selected')" data-test="sheet-selected" [label]="'Only ' + (one.territory.name)"><span brMenuItemPrefix aria-hidden="true" class="tr-dot" [style.background]="displayColor(one.territory.color)"></span></button>
        <button brMenuItem *ngIf="rosterNarrowed"
                (triggered)="runSheets(action, 'shown')" data-test="sheet-shown" [label]="sheetScopeLabel('shown')"></button>
        <button brMenuItem (triggered)="runSheets(action, 'all')" data-test="sheet-all" [label]="sheetScopeLabel('all')"></button>
        <p class="tr-sheet-note" *ngIf="dirty">
          Lead lists come from the saved map, so unsaved changes are not in them.
        </p>
      </br-menu>
    </ng-template>

    <div class="br-alert br-alert--danger tr-feedback" *ngIf="drawProblem">
      <br-icon>report_problem</br-icon>
      <span>
        {{ drawProblem }}
        <b> Trace it again without crossing back over your own line.</b>
      </span>
    </div>

    <div class="br-alert tr-feedback tr-feedback--success" *ngIf="seamNote && !drawing">
      <br-icon>link</br-icon><span>{{ seamNote }}</span>
    </div>

    <section class="br-alert br-alert--danger tr-conflicts" *ngIf="conflicts.length">
      <h5>Someone else changed the map</h5>
      <p *ngFor="let c of conflicts">
        <b>{{ c.name }}</b> moved while you were editing. Refresh and redo this
        change — nothing was saved.
      </p>
      <button type="button" class="br-btn br-btn--outline-danger br-btn--sm" (click)="load()">Refresh</button>
    </section>

    <mat-progress-bar mode="indeterminate" *ngIf="loading || sheetBusy"></mat-progress-bar>
    <div class="br-alert br-alert--danger tr-feedback" *ngIf="loadError">
      <br-icon>error_outline</br-icon><span>{{ loadError }}</span>
    </div>
    <div class="br-alert br-alert--danger tr-feedback" *ngIf="saveError">
      <br-icon>block</br-icon><span>{{ saveError }}</span>
    </div>
    <div class="br-alert br-alert--danger tr-feedback" *ngIf="sheetError">
      <br-icon>error_outline</br-icon><span>{{ sheetError }}</span>
    </div>

    <div class="tr-layout" *ngIf="!loadError">

    <!-- The roster first. It is the list you choose FROM, and the section under
         it is what you chose - so reading down the page is the order the work
         happens in. It also stops the page from opening on a detail panel with
         its own Leads table while the thing that panel belongs to is somewhere
         below the fold. -->
    <ng-container *ngIf="!boardOpen">
    <section class="tr-section">

      <div class="br-card-body tr-roster">

      <!-- WHAT WOULD HAPPEN, before it happens, and named. "Merge 2 into
           North" is checkable against the ticks; "Merge selected" is not, and
           which one survives is the whole question - the first ticked keeps
           its name, color and rep, and the others give up their ground and
           their leads. -->
      <div class="tr-mergebar" *ngIf="mergePicks.length >= 2" data-test="merge-bar">
        <span class="tr-mergebar-say">
          Merge {{ mergePicks.length - 1 }}
          {{ mergePicks.length === 2 ? 'territory' : 'territories' }} into
          <b>{{ mergeSurvivor?.territory?.name }}</b>, which keeps its name,
          color and rep.
        </span>
        <button type="button" class="br-btn br-btn--primary br-btn--sm"
                [disabled]="merging" (click)="mergeNow()"
                data-test="merge-go">
          {{ merging ? 'Merging…' : 'Merge' }}
        </button>
        <button type="button" class="br-btn br-btn--ghost br-btn--sm"
                [disabled]="merging" (click)="clearPicks()">Clear</button>
      </div>

      <!-- Written at once and undoable at once. The absorbed territories are
           gone from the table, so this is the only chance to put them back -
           and even then they come back as new records, which the offer says
           rather than letting somebody discover it later. -->
      <div class="tr-mergedone" *ngIf="lastMerge" data-test="merge-done">
        <span>{{ mergeNote }}</span>
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                [disabled]="merging" (click)="undoMerge()"
                data-test="merge-undo"
                matTooltip="Puts them back as new records - merging deleted the originals - with their outlines, their details and their leads. Only until you leave this page.">
          {{ merging ? 'Undoing…' : 'Undo' }}
        </button>
      </div>

      <!-- Row, tick and removal are siblings, not nested: the row is a button,
           and a button inside a button is not a thing a browser can make sense
           of. They share the highlight so they still read as one row. -->
      <div class="tr-row-wrap" *ngFor="let row of rosterRows"
           [class.tr-row-on]="row.territory.id === selectedId"
           [class.tr-row-picked]="isPicked(row.territory.id)">
      <label class="tr-row-pick"
             [attr.data-test]="'pick-' + row.territory.id"
             [title]="isPicked(row.territory.id)
               ? 'Ticked for merging'
               : 'Tick to merge. The first one ticked is the one that stays.'">
        <input type="checkbox" [checked]="isPicked(row.territory.id)"
               (change)="togglePick(row.territory.id)"
               [attr.aria-label]="'Merge ' + row.territory.name" />
        <!-- The order IS the decision, so it is on screen rather than in
             somebody's memory of what they clicked first. -->
        <span class="tr-pick-no" *ngIf="isPicked(row.territory.id)">{{
          mergePicks.indexOf(row.territory.id) + 1 }}</span>
      </label>
      <button type="button" class="tr-row" (click)="select(row)">
        <span class="tr-dot" [style.background]="displayColor(row.territory.color)"></span>
        <span class="tr-row-main">
          <span class="tr-row-name">{{ row.territory.name }}</span>
          <span class="tr-row-meta">
            <span [style.color]="statusColor(row.territory.status)">{{ row.territory.status }}</span>
            <span *ngIf="row.territory.assignedUserId != null">
              · {{ repLabel(row.territory.assignedUserId) }}</span>
            <span *ngIf="row.territory.assignedUserId == null" class="tr-unassigned-tag">· unassigned</span>
          </span>
        </span>
        <!-- The same two numbers the map draws on the patch, dotted in the same
             two colours, so a row and its chip are plainly one fact. -->
        <span class="tr-row-nums">
          <b class="tr-row-counts">
            <span class="tr-row-count tr-row-count--lead"
                  [title]="row.leads + ' leads'">{{ row.leads }}</span>
            <span class="tr-row-count tr-row-count--customer"
                  *ngIf="row.customers != null"
                  [title]="row.customers + ' customers'">{{ row.customers }}</span>
          </b>
          <span>{{ row.openPipelineValue | currency:'USD':'symbol':'1.0-0' }}</span>
        </span>
      </button>

      <!-- Removing a patch used to mean selecting it first and scrolling past
           its fields, its boundary notice and its whole leads table to reach
           the button at the foot - four screens to throw away a row you were
           already looking at. It is the same staged removal either way.

           Quiet until the row is approached, because this is a list of
           territories rather than a list of delete buttons; focus brings it up
           too, so it is reachable by Tab and not only by pointer. -->
      <!-- WHO OWNS IT, changed where you compare them. The plan deals reps
           out round-robin, which is a starting point; "this one to her" is
           the decision, and making it used to mean opening the territory and
           editing a field, once per territory. Staged like every other edit,
           so it joins the same Save. -->
      <button type="button" class="tr-row-rep"
              [brMenuTriggerFor]="repMenu"
              [brMenuTriggerData]="{ patch: row }"
              [attr.aria-label]="'Rep for ' + row.territory.name"
              matTooltip="Assign a rep"
              data-test="row-rep">
        <br-icon>person</br-icon>
      </button>
      <button type="button" class="tr-row-remove"
              (click)="remove(row)"
              [attr.aria-label]="'Remove ' + row.territory.name"
              matTooltip="Stage a removal. Nothing is deleted until you press Save the map.">
        <br-icon>delete_outline</br-icon>
      </button>
      </div>

      <ng-template #repMenu let-patch="patch">
        <br-menu aria-label="Assign representative">
          <button brMenuItem (triggered)="assignRep(patch, null)" label="Unassigned"></button>
          <button brMenuItem *ngFor="let r of reps"
                  (triggered)="assignRep(patch, r.userId)" [label]="r.displayName || r.userName" [icon]="(patch.territory.assignedUserId === r.userId) ? ('check') : ''"></button>
        </br-menu>
      </ng-template>

      <!-- The list, folded away while a patch is open so its detail has the
           window. It folds WHOLE now, the open patch included, because the
           section below already names that one - so the control says how many
           there are rather than how many "more" there are, which would be
           counting against a row that is no longer on screen. -->
      <button type="button" class="tr-more" *ngIf="foldedAway > 0"
              (click)="openRoster()"
              [matTooltip]="'Show all ' + foldedAway + ' territories'">
        <span class="tr-more-mark">+</span>
        <span class="tr-more-text">Show all {{ foldedAway }}</span>
      </button>

      <!-- A roster emptied BY A FILTER is not a company with no territories,
           and the difference is the whole message: one is answered by drawing
           patches, the other by taking a rule off. Reported as a page that
           looked broken when a rule matched nothing. -->
      <div class="tr-empty" *ngIf="rows.length && !visibleRows.length && !loading">
        <p class="tr-note">
          No territory matches the filters on this list.
        </p>
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                (click)="clearFilters()">Clear the filters</button>
      </div>

      <div class="tr-empty" *ngIf="!rows.length && !loading && !mineOnly">
        <p class="tr-note">
          No territories yet. Let the agent cut balanced patches from where your
          leads actually are, then adjust anything you disagree with.
        </p>
        <!-- The toolbar's copy of this button is on screen at the same time as
             this one - the empty state shows precisely when there is nothing to
             list - so one label in two treatments would be visible at once. -->
        <button type="button" class="br-btn br-btn--outline-primary"
                [disabled]="planning" (click)="planWithAgent()">
          <br-icon>auto_awesome</br-icon>
          {{ planning ? 'Planning…' : 'Planning Territory' }}
        </button>
      </div>
      </div>
    </section>

    <section class="tr-section" *ngIf="selected as row">
      <header class="br-card-header tr-section-head">
        <h3 class="br-card-title br-m-0">
          <span class="tr-dot" [style.background]="displayColor(row.territory.color)"></span>
          <span class="tr-detail tr-detail-name" *ngIf="editingField !== 'name'"
                (click)="editField('name')" title="Rename">{{ form.name }}</span>
          <input *ngIf="editingField === 'name'" class="tr-detail-input tr-detail-name"
                 [(ngModel)]="form.name" (keyup.enter)="commitField()"
                 (keyup.escape)="cancelField()" (blur)="commitField()" autofocus />
        </h3>
        <!-- The territory's two properties, on the same line as its name.
             They were a second strip under the head, which spent a whole row
             of a panel whose subject is a boundary on two short words - and
             put the boundary notice and the leads table that far further down.

             Dropdowns at rest, not text that turns into one when clicked. The
             click-to-edit read as a caption: a value with no control around it
             says "this is what it is", not "this is what you can set", and it
             was reported as a status bar nobody could use. A closed list says
             both at once - what it is now, and that there is a list behind it.

             Still two separately addressable fields rather than one dialog:
             design.md's third principle asks that every action be reachable by
             code, and an agent has to be able to set the rep without driving
             an editor for both. -->
        <div class="tr-field" data-field="status">
          <span class="tr-field-label" id="tr-field-status">Status</span>
          <!-- Still tinted, as it is in the roster below: Draft, Active and
               Retired are the three states of a territory's life, and the
               colour is how a panel says which one at a glance. A dropdown
               that is only ever grey makes the reader read the word. -->
          <mat-select class="tr-head-select" [(ngModel)]="form.status"
                      [style.color]="statusColor(form.status)"
                      (selectionChange)="saveDetails()"
                      aria-labelledby="tr-field-status">
            <mat-option *ngFor="let s of statuses" [value]="s">{{ s }}</mat-option>
          </mat-select>
        </div>

        <div class="tr-field" data-field="rep">
          <span class="tr-field-label" id="tr-field-rep">Rep</span>
          <!-- The empty state offers rather than reports. "unassigned" is an
               absence written in the same grammar and weight as a fact;
               "Assign a rep" is the same information as an invitation, and it
               says the field can be changed at all. -->
          <mat-select class="tr-head-select" [(ngModel)]="form.assignedUserId"
                      [disabled]="!reps.length" placeholder="Assign a rep"
                      (selectionChange)="saveDetails()"
                      aria-labelledby="tr-field-rep">
            <mat-option [value]="null">Unassigned</mat-option>
            <mat-option *ngFor="let r of reps" [value]="r.userId">
              {{ r.displayName || r.userName }}
            </mat-option>
          </mat-select>
        </div>

        <span class="tr-section-facts br-text-muted">
          {{ row.leads }} leads<span *ngIf="row.customers != null">
            · {{ row.customers }} customers</span>
          · {{ row.areaSqKm | number:'1.0-1' }} km²
          · {{ row.openPipelineValue | currency:'USD':'symbol':'1.0-0' }} open
        </span>
        <button type="button" class="br-btn br-btn--ghost br-icon-btn"
                (click)="clearSelection()" aria-label="Deselect">
          <br-icon>close</br-icon>
        </button>
      </header>

      <div class="br-card-body">
      <!-- No colour control here. A territory still HAS a colour - it is the
           dot beside its name and the shade of its shape on the map, and a new
           one is still given a colour of its own - but choosing it was a
           control on the panel for a decision made about once in a
           territory's life, sitting beside the two that get changed. -->

      <div class="br-alert tr-feedback tr-feedback--warning" *ngIf="boundaryIssue">
        <br-icon>{{ boundaryIssue === 'missing' ? 'crop_free' : 'broken_image' }}</br-icon>
        <span class="tr-noboundary-say" *ngIf="boundaryIssue === 'missing'">
          <b>No boundary yet.</b> This territory has no shape on the map, so there
          is nothing to reshape and no lead falls inside it. Draw one and it starts
          collecting.
        </span>
        <span class="tr-noboundary-say" *ngIf="boundaryIssue === 'unusable'">
          <b>This boundary cannot be reshaped.</b> One is stored, but it is not a
          polygon this page can put corners on. Drawing a new one replaces it.
        </span>
        <button type="button" class="br-btn br-btn--primary br-btn--sm"
                (click)="startFreehand(row.territory.id)">
          <br-icon>gesture</br-icon>
          {{ boundaryIssue === 'missing' ? 'Sketch its boundary' : 'Sketch a new one' }}
        </button>
      </div>

      <!-- Removal lives on the row in the list above, beside every other
           territory's, where merging and deleting are chosen together. A
           second way in at the foot of the open patch was the older one, from
           when the list had none - two buttons for one staged removal, and
           the far one at the end of a leads table. -->

      <div class="tr-prospects">
        <div class="tr-prospects-bar">
          <h4>Leads <span class="tr-count">{{ prospectTotal }}</span></h4>
        </div>

        <div class="tr-prospects-table">
          <ng-container *workspaceView="'full'">
<app-data-table [(search)]="prospectSearch" #prospectTable
                        [columns]="prospectColumns"
                        [fetch]="fetchProspects"
                        [defaultHiddenColumns]="prospectHiddenColumns"
                        columnPickerKey="territory-lead-list"
                        resizableColumns
                        searchPlaceholder="Search leads"
                        [rowActions]="prospectActionsTpl"
                        [pageSize]="prospectPageSize"
                        [pageSizeOptions]="[25, 50, 100]"
                        emptyText="No leads in this territory."
                        [filters]="prospectFilterValues">
          <!-- The Leads page's Filters bar, on the Leads page's own table: the
               same columns, the same rules, the same endpoint. -->
          <app-data-filter contextKey="territory-prospects" dt-filters #prospectFilter
                           [fields]="prospectFilterFields"
                           [values]="prospectFilterValues"
                           (change)="prospectFilter.apply()"
                           (applied)="applyProspectFilters($event)"></app-data-filter>

          <!-- The leads table's cells, so a lead reads the same under its
               patch as it does on /leads/table. City, State and ZIP are not
               here on purpose: the table prints a plain field itself, and a
               template that only repeats the value is one more place to break.
               Sales rep is the exception - this page knows the reps by name,
               so it prints the name rather than the user id. -->
          <ng-template dtCell="title" let-row>
            <button type="button" class="tr-open-btn" (click)="openLead(row)">
              {{ row.title || 'Lead ' + row.leadId }}</button>
          </ng-template>
          <ng-template dtCell="address" let-row>{{ row.address || '—' }}</ng-template>
          <ng-template dtCell="boardColumn" let-row>
            <span class="tr-stage-badge" [style.color]="leadStageColor(row.boardColumnCode)">
              {{ row.boardColumn }}</span>
          </ng-template>
          <ng-template dtCell="origin" let-row>{{ row.origin }}</ng-template>
          <ng-template dtCell="sources" let-row>{{ sourceLabel(row) }}</ng-template>
          <ng-template dtCell="businessTypes" let-row>{{ typeLabels(row.businessTypes) || '—' }}</ng-template>
          <ng-template dtCell="salesRep" let-row>
            {{ row.salesRepUserId != null ? repLabel(row.salesRepUserId) : '—' }}
          </ng-template>
          <ng-template dtCell="score" let-row>{{ row.qualificationScore ?? '—' }}</ng-template>
          <ng-template dtCell="contribution" let-row>
            {{ row.contributionBase != null ? (row.contributionBase | currency:'USD':'symbol':'1.0-0') : '—' }}
          </ng-template>
          <ng-template dtCell="routeFit" let-row>{{ row.routeFitStatus ?? '—' }}</ng-template>
          <ng-template dtCell="nextAction" let-row>
            <span [class.tr-overdue]="overdue(row)">
              {{ row.nextActionType ?? '—' }}<ng-container *ngIf="row.nextActionDue"> · {{ row.nextActionDue | date:'MMM d' }}</ng-container>
            </span>
          </ng-template>
          <ng-template dtCell="probability" let-row>
            {{ row.probability != null ? (row.probability | percent:'1.0-0') : '—' }}
          </ng-template>
          <ng-template dtCell="discoveredAt" let-row>
            {{ row.discoveredAt | date:'MMM d, y' }}
          </ng-template>
          </app-data-table>
</ng-container>
<ng-container *workspaceView="'compact'">

<app-data-list #compactList [fetch]="fetchProspects" [(search)]="prospectSearch" [pageSize]="prospectPageSize">

<ng-template dataListItem let-row>
  <div class="br-flex br-items-center br-justify-between br-gap-2">
    <strong>{{ row.title || ('Lead #' + row.leadId) }}</strong>
    <app-map-pin [latitude]="row.lat" [longitude]="row.lng"
                 [object]="{ featureKey: 'leads', objectId: row.leadId }"
                 [popup]="{ type: 'Lead', title: row.title || ('Lead #' + row.leadId), subtitle: row.address || undefined }"></app-map-pin>
  </div>
  <div class="br-text-muted">{{ row.address || 'No address' }}</div>
</ng-template>
<ng-template dataListDetail let-row>
        <app-lead-page [inline]="true" [leadId]="row.leadId"
                       (profileLoaded)="row.title = $event.name; row.address = $event.addressLine1; row.lat = $event.latitude; row.lng = $event.longitude">
        </app-lead-page>
</ng-template>
</app-data-list>

</ng-container>
        </div>

        <ng-template #prospectActionsTpl let-row>
          <button type="button" class="br-btn br-btn--ghost br-icon-btn"
                  matTooltip="View" aria-label="View lead" (click)="openLead(row)">
            <br-icon>visibility</br-icon>
          </button>
        </ng-template>


      </div>
      </div>
    </section>
    </ng-container>

    <!-- THE BOARD, WHICH IS THE WHOLE PAGE WHILE IT IS OPEN.
         A column per territory and a card per lead: it answers "how do these
         fall across the patches", which is a question about all of them. It
         used to render inside the open patch's Leads section, so the view of
         everything was reachable only after choosing one thing - and on a
         page that opens with nothing chosen, that is a view most people never
         saw. -->
    <section class="tr-section tr-board-page" *ngIf="boardOpen">
      <div class="br-card-body">
        <div class="tr-board">
          <app-board-column class="tr-board-col" *ngFor="let column of boardColumns"
                            [class.tr-board-here]="column.row.territory.id === selectedId"
                            [data]="column.row">
            <div class="tr-board-head">
              <span class="tr-board-dot"
                    [style.background]="column.row.territory.color || '#607d8b'"></span>
              <span class="tr-board-name">{{ column.row.territory.name }}</span>
              <span class="tr-count">{{ column.total }}</span>
            </div>

              <div class="tr-board-card" *ngFor="let lead of column.leads">
                <span class="tr-board-title">{{ lead.title }}</span>
                <span class="tr-board-sub">{{ lead.boardColumn }}</span>
              </div>

              <p class="tr-board-empty"
                 *ngIf="!column.leads.length && !column.loading">No leads here yet.</p>
              <p class="tr-board-empty" *ngIf="column.loading">Loading&hellip;</p>
              <!-- The count is the truth; the cards are the part you can act
                   on. Saying so beats a column that looks short. -->
              <p class="tr-board-more" *ngIf="column.total > column.leads.length">
                Showing {{ column.leads.length }} of {{ column.total }}. Use the list to see them all.
              </p>
          </app-board-column>
        </div>
      </div>
    </section>
    </div>

  </div>
</div>
